import React, { createContext, useContext, useState, useEffect } from "react"
import axios from "axios"
// import { baseApi } from "../config"
import { toast } from "react-toastify"

const baseApi = process.env.REACT_APP_API_BASE + "/api/v1"

const DemoContext = createContext()

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("demo_token")
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export const DemoProvider = ({ children }) => {
  const [demoLoading, setDemoLoading] = useState(true)
  const [hasDemoAccess, setHasDemoAccess] = useState(false)
  const [demoData, setDemoData] = useState(null)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [feedbackTimerStarted, setFeedbackTimerStarted] = useState(false)

  // Load demo summary data
  const loadDemo = async () => {
    try {
      const res = await axios.get(`${baseApi}/demo/summary`)

      setHasDemoAccess(true)
      setDemoData(res.data.demoData)
    } catch (err) {
      console.error("Error loading demo data:", err)
      setHasDemoAccess(false)
      setDemoData(null)
    } finally {
      setDemoLoading(false)
    }
  }

  // Check demo access cookie
  const checkDemo = async () => {
    const token = localStorage.getItem("demo_token")

    if (!token) {
      setHasDemoAccess(false)
      setDemoLoading(false)
      setFeedbackSubmitted(false)

      return
    }

    try {
      const res = await axios.get(`${baseApi}/demo/check`)

      if (!res.data.hasAccess) {
        localStorage.removeItem("demo_token")
        setHasDemoAccess(false)
        setDemoLoading(false)
        setFeedbackSubmitted(false)

        return
      }

      if (res.data.expiresAt && Date.now() > res.data.expiresAt) {
        localStorage.removeItem("demo_token")
        setHasDemoAccess(false)
        setDemoLoading(false)
        setFeedbackSubmitted(false)

        return
      }

      setFeedbackSubmitted(!!res.data.feedbackSubmitted)

      await loadDemo()
    } catch (err) {
      console.error("Error checking demo access:", err)
      localStorage.removeItem("demo_token")
      setHasDemoAccess(false)
      setDemoLoading(false)
      setFeedbackSubmitted(false)
    }
  }

  // Request demo access with email
  const requestDemoAccess = async (email, phone) => {
    if (!email) {
      toast.error("Please enter a valid email.")
      return
    }

    if (!phone) {
      toast.error("Please enter a valid phone number.")
      return
    }

    try {
      const res = await axios.post(`${baseApi}/demo/access`, { email, phone })

      if (res.data.success) {
        localStorage.setItem("demo_token", res.data.token)
        toast.success("Demo access granted!")
        setHasDemoAccess(true)
        setFeedbackSubmitted(!!res.data.feedbackSubmitted)

        await loadDemo()
      } else {
        toast.warn(res.data.message || "Failed to get demo access")
      }
    } catch (err) {
      console.error("Demo access error:", err)
      const message = err.response?.data?.message || err.response?.data?.error || "Failed to access demo. Please try again later."
      toast.error(message)
      setHasDemoAccess(false)
    }
  }

  const submitDemoFeedback = async (payload) => {
    try {
      const res = await axios.post(`${baseApi}/demo/feedback`, payload)

      if (res.data.success) {
        toast.success("Feedback submitted successfully!")
        setFeedbackSubmitted(true)

        return true
      } else {
        toast.warn(res.data.message || "Failed to submit feedback")
        return false
      }
    } catch (err) {
      console.error("Feedback error:", err)

      const message = err.response?.data?.message || err.response?.data?.error || "Failed to submit feedback. Please try again."

      toast.error(message)
      return false
    }
  }

  useEffect(() => {
    checkDemo()
  }, [])

  return (
    <DemoContext.Provider
      value={{
        setFeedbackTimerStarted,
        feedbackTimerStarted,
        demoLoading,
        hasDemoAccess,
        demoData,
        feedbackSubmitted,
        refreshDemo: loadDemo,
        requestDemoAccess,
        submitDemoFeedback,
      }}
    >
      {children}
    </DemoContext.Provider>
  )
}

export const useDemo = () => useContext(DemoContext)
